/**
 * GROUP messaging via "sender keys" (Signal model for large groups).
 *
 * Instead of N 1:1 sessions per message, each member has ONE sending chain
 * (sender key) for the group:
 *   - **chain key**: ratcheted (symmetric forward secrecy) on each message;
 *     each message derives its own message key and discards it.
 *   - **signing key** (ECDSA): authenticates the sender to the rest (prevents
 *     one member from impersonating another).
 *
 * The sender key is DISTRIBUTED once to each member through their 1:1 session (double
 * ratchet), so the distribution is also E2E. When someone joins/leaves it is
 * rotated (new sender key redistributed) → the one who left cannot decrypt the new stuff.
 *
 * Each group send is 1 encryption + 1 signature (not N); each receiver keeps the
 * reception state per (group, sender) and tolerates out-of-order with skipped keys.
 */

import { base64ToBytes, bytesToBase64, concatBytes } from "./crypto/encoding";
import { generateSigningKeyPair, type RawKeyPair, sign, verify } from "./crypto/dh";
import { aeadOpen, aeadSeal, hmac, importAesKey, randomBytes } from "./crypto/primitives";

const MAX_SKIP = 512;

export interface SenderKeyState {
  readonly groupId: string;
  readonly senderId: string;
  chainKey: Uint8Array;
  iteration: number;
  readonly signing: RawKeyPair;
}

/** E2E message that distributes a sender's sender key to a member (over their 1:1 session). */
export interface SenderKeyDistribution {
  readonly groupId: string;
  readonly senderId: string;
  readonly chainKey: string;
  readonly iteration: number;
  readonly signingPub: string;
}

export interface GroupMessage {
  readonly groupId: string;
  readonly senderId: string;
  readonly iteration: number;
  readonly ciphertext: string;
  readonly signature: string;
}

interface ReceiverState {
  chainKey: Uint8Array;
  iteration: number;
  signingPub: Uint8Array;
  skipped: Map<number, Uint8Array>;
}

function aadFor(groupId: string, senderId: string): string {
  return `susurro:group:v1:${groupId}:${senderId}`;
}

async function chainStep(chainKey: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const next = await hmac(chainKey, new Uint8Array([0x02]));
  const messageKey = await hmac(chainKey, new Uint8Array([0x01]));
  return [next, messageKey];
}

function signPayload(ciphertext: Uint8Array, iteration: number): Uint8Array {
  const meta = new Uint8Array(4);
  new DataView(meta.buffer).setUint32(0, iteration, false);
  return concatBytes(ciphertext, meta);
}

// --- Sender side ------------------------------------------------------------

export async function createSenderKey(groupId: string, senderId: string): Promise<SenderKeyState> {
  return {
    groupId,
    senderId,
    chainKey: randomBytes(32),
    iteration: 0,
    signing: await generateSigningKeyPair(),
  };
}

/** Distribution message to spread over the 1:1 sessions (contains SECRET material). */
export function distributionMessage(state: SenderKeyState): SenderKeyDistribution {
  return {
    groupId: state.groupId,
    senderId: state.senderId,
    chainKey: bytesToBase64(state.chainKey),
    iteration: state.iteration,
    signingPub: bytesToBase64(state.signing.publicKey),
  };
}

export async function encryptGroupMessage(
  state: SenderKeyState,
  plaintext: Uint8Array,
): Promise<GroupMessage> {
  const [next, messageKey] = await chainStep(state.chainKey);
  const { iteration } = state;
  state.chainKey = next;
  state.iteration += 1;

  const key = await importAesKey(messageKey, ["encrypt"]);
  const ciphertext = await aeadSeal(key, plaintext, aadFor(state.groupId, state.senderId));
  const signature = await sign(state.signing.privateKey, signPayload(ciphertext, iteration));
  return {
    groupId: state.groupId,
    senderId: state.senderId,
    iteration,
    ciphertext: bytesToBase64(ciphertext),
    signature: bytesToBase64(signature),
  };
}

// --- Receiver side ----------------------------------------------------------

export function acceptDistribution(dist: SenderKeyDistribution): ReceiverState {
  return {
    chainKey: base64ToBytes(dist.chainKey),
    iteration: dist.iteration,
    signingPub: base64ToBytes(dist.signingPub),
    skipped: new Map(),
  };
}

async function skipTo(state: ReceiverState, until: number): Promise<void> {
  if (until - state.iteration > MAX_SKIP) {
    throw new Error("group: demasiadas claves saltadas (posible DoS)");
  }
  while (state.iteration < until) {
    const [next, messageKey] = await chainStep(state.chainKey);
    state.skipped.set(state.iteration, messageKey);
    state.chainKey = next;
    state.iteration += 1;
  }
}

export async function decryptGroupMessage(
  state: ReceiverState,
  msg: GroupMessage,
): Promise<Uint8Array> {
  const ciphertext = base64ToBytes(msg.ciphertext);
  const signature = base64ToBytes(msg.signature);
  if (!(await verify(state.signingPub, signPayload(ciphertext, msg.iteration), signature))) {
    throw new Error("group: invalid sender signature");
  }

  let messageKey: Uint8Array;
  const cached = state.skipped.get(msg.iteration);
  if (cached) {
    state.skipped.delete(msg.iteration);
    messageKey = cached;
  } else {
    await skipTo(state, msg.iteration);
    const [next, mk] = await chainStep(state.chainKey);
    state.chainKey = next;
    state.iteration += 1;
    messageKey = mk;
  }

  const key = await importAesKey(messageKey, ["decrypt"]);
  return aeadOpen(key, ciphertext, aadFor(msg.groupId, msg.senderId));
}

export type { ReceiverState as GroupReceiverState };
